/**
 * Animation Utility Layer
 *
 * Centralized GSAP animation functions for the Minihold website.
 * All animations respect prefers-reduced-motion and are wrapped in try/catch
 * to prevent errors from breaking page content.
 *
 * Duration caps:
 * - Hero entrance: ≤1000ms total (150ms stagger)
 * - Scroll reveal: ≤600ms
 * - Page transitions: ≤600ms combined (300ms each)
 * - Hover animations: ≤300ms
 *
 * Requirements: 6.1, 6.2, 6.3, 6.4, 6.5, 6.6, 6.7, 15.4
 */

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// ─── Types ───────────────────────────────────────────────────────────────────

export interface AnimationConfig {
  duration: number;
  ease: string;
  stagger?: number;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const HERO_STAGGER = 0.15; // 150ms between elements
const HERO_MAX_DURATION = 1; // 1000ms total cap
const SCROLL_REVEAL_DURATION = 0.6; // 600ms
const SCROLL_REVEAL_THRESHOLD = 0.2; // 20% of element visible
const PAGE_TRANSITION_DURATION = 0.3; // 300ms each (600ms combined)
const HOVER_DURATION = 0.3; // 300ms
const PARALLAX_SPEED = 0.5; // 50% scroll speed

const DEFAULT_EASE = 'power2.out';

// ─── Reduced Motion Check ────────────────────────────────────────────────────

/**
 * Checks if the user prefers reduced motion.
 * Returns true when the OS-level prefers-reduced-motion: reduce is active.
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// ─── Hero Entrance Animation ─────────────────────────────────────────────────

/**
 * Animates hero section elements with a staggered entrance.
 * Each child element appears 150ms after the previous one.
 * Total sequence completes within 1000ms.
 *
 * Requirement 6.1: Hero entrance with 150ms stagger, total ≤1000ms
 * Requirement 6.7: No layout shift during/after animations
 * Requirement 15.4: No content flashing more than 3 times per second
 */
export function animateHeroEntrance(container: HTMLElement): gsap.core.Timeline {
  try {
    const tl = gsap.timeline();

    if (prefersReducedMotion()) {
      // Show content immediately without motion
      tl.set(container.children, { opacity: 1, y: 0 });
      return tl;
    }

    const children = container.children;
    const childCount = children.length;

    // Calculate per-element duration to stay within 1000ms total
    // Total time = duration + stagger * (childCount - 1)
    // Solve for duration: duration = maxTotal - stagger * (childCount - 1)
    const staggerTotal = HERO_STAGGER * Math.max(0, childCount - 1);
    const perElementDuration = Math.min(0.6, Math.max(0.2, HERO_MAX_DURATION - staggerTotal));

    tl.from(children, {
      opacity: 0,
      y: 30,
      duration: perElementDuration,
      stagger: HERO_STAGGER,
      ease: DEFAULT_EASE,
    });

    return tl;
  } catch {
    // If animation fails, ensure content is visible
    gsap.set(container.children, { opacity: 1, y: 0 });
    return gsap.timeline();
  }
}

// ─── Scroll Reveal ───────────────────────────────────────────────────────────

/**
 * Creates a scroll-triggered reveal animation for a content section.
 * Triggers when 20% of the element is visible in the viewport.
 * Animation duration capped at 600ms.
 *
 * Requirement 6.2: Scroll reveal at 20% threshold, ≤600ms duration
 * Requirement 6.7: No layout shift during/after animations
 */
export function createScrollReveal(
  element: HTMLElement,
  config?: Partial<AnimationConfig>,
): ScrollTrigger {
  try {
    if (prefersReducedMotion()) {
      // Show content immediately, return a no-op ScrollTrigger
      gsap.set(element, { opacity: 1, y: 0 });
      return ScrollTrigger.create({ trigger: element });
    }

    const duration = Math.min(config?.duration ?? SCROLL_REVEAL_DURATION, 0.6);
    const ease = config?.ease ?? DEFAULT_EASE;

    // Set initial hidden state
    gsap.set(element, { opacity: 0, y: 30 });

    const reveal = () => {
      gsap.to(element, {
        opacity: 1,
        y: 0,
        duration,
        ease,
      });
    };

    const trigger = ScrollTrigger.create({
      trigger: element,
      start: `top ${(1 - SCROLL_REVEAL_THRESHOLD) * 100}%`,
      once: true,
      onEnter: reveal,
    });

    // onEnter only fires when the element crosses the start line while scrolling.
    // Elements already inside the viewport on load (above the fold, user never
    // scrolls) have already passed it, so reveal those immediately.
    if (element.getBoundingClientRect().top < window.innerHeight * (1 - SCROLL_REVEAL_THRESHOLD)) {
      reveal();
    }

    return trigger;
  } catch {
    // If animation fails, ensure content is visible
    gsap.set(element, { opacity: 1, y: 0 });
    return ScrollTrigger.create({ trigger: element });
  }
}

// ─── Page Transitions ────────────────────────────────────────────────────────

/**
 * Animates page exit transition (fade out + slight upward movement).
 * Duration capped at 300ms (half of 600ms combined budget).
 *
 * Requirement 6.3: Page transitions exit+entrance combined ≤600ms
 */
export function pageTransitionExit(container: HTMLElement): Promise<void> {
  try {
    if (prefersReducedMotion()) {
      gsap.set(container, { opacity: 0 });
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      gsap.to(container, {
        opacity: 0,
        y: -20,
        duration: PAGE_TRANSITION_DURATION,
        ease: DEFAULT_EASE,
        onComplete: resolve,
      });
    });
  } catch {
    gsap.set(container, { opacity: 0 });
    return Promise.resolve();
  }
}

/**
 * Animates page entrance transition (fade in from below).
 * Duration capped at 300ms (half of 600ms combined budget).
 *
 * Requirement 6.3: Page transitions exit+entrance combined ≤600ms
 */
export function pageTransitionEntrance(container: HTMLElement): Promise<void> {
  try {
    if (prefersReducedMotion()) {
      gsap.set(container, { opacity: 1, y: 0 });
      return Promise.resolve();
    }

    // Set initial state for entrance
    gsap.set(container, { opacity: 0, y: 20 });

    return new Promise((resolve) => {
      gsap.to(container, {
        opacity: 1,
        y: 0,
        duration: PAGE_TRANSITION_DURATION,
        ease: DEFAULT_EASE,
        onComplete: () => {
          // Remove the residual inline transform/will-change. A non-`none`
          // transform on this wrapper makes it the containing block for fixed
          // positioning, which breaks descendants' `background-attachment: fixed`
          // (the homepage parallax dividers) after client-side navigation.
          gsap.set(container, { clearProps: 'transform,willChange' });
          resolve();
        },
      });
    });
  } catch {
    gsap.set(container, { opacity: 1, y: 0 });
    return Promise.resolve();
  }
}

// ─── Hover Animations ────────────────────────────────────────────────────────

/**
 * Applies hover animation to an interactive element (cards, buttons, links).
 * Duration capped at 300ms.
 *
 * Requirement 6.4: Hover animations ≤300ms
 * Requirement 6.7: No layout shift during/after animations
 */
export function applyHoverAnimation(element: HTMLElement): void {
  try {
    if (prefersReducedMotion()) {
      return;
    }

    const handleMouseEnter = () => {
      gsap.to(element, {
        scale: 1.02,
        duration: HOVER_DURATION,
        ease: DEFAULT_EASE,
      });
    };

    const handleMouseLeave = () => {
      gsap.to(element, {
        scale: 1,
        duration: HOVER_DURATION,
        ease: DEFAULT_EASE,
      });
    };

    element.addEventListener('mouseenter', handleMouseEnter);
    element.addEventListener('mouseleave', handleMouseLeave);

    // Store cleanup references on the element for later removal
    (element as any).__hoverCleanup = () => {
      element.removeEventListener('mouseenter', handleMouseEnter);
      element.removeEventListener('mouseleave', handleMouseLeave);
      gsap.set(element, { scale: 1 });
    };
  } catch {
    // Hover animation failure is non-critical; element remains functional
  }
}

// ─── Parallax Effect ─────────────────────────────────────────────────────────

/**
 * Creates a parallax scroll effect on a decorative element.
 * Element scrolls at 50% of viewport scroll speed by default.
 *
 * Requirement 6.6: Parallax at 50% scroll speed
 * Requirement 6.7: No layout shift during/after animations
 */
export function createParallax(
  element: HTMLElement,
  speed: number = PARALLAX_SPEED,
): ScrollTrigger {
  try {
    if (prefersReducedMotion()) {
      return ScrollTrigger.create({ trigger: element });
    }

    // Clamp speed to prevent excessive movement (safety for Req 15.4)
    const clampedSpeed = Math.min(Math.max(speed, 0), 1);

    const trigger = ScrollTrigger.create({
      trigger: element,
      start: 'top bottom',
      end: 'bottom top',
      scrub: true,
      onUpdate: (self) => {
        const yOffset = self.progress * 100 * clampedSpeed;
        gsap.set(element, { y: -yOffset });
      },
    });

    return trigger;
  } catch {
    return ScrollTrigger.create({ trigger: element });
  }
}

// ─── Cleanup ─────────────────────────────────────────────────────────────────

/**
 * Cleans up ScrollTrigger instances for a component on unmount.
 * Prevents memory leaks from orphaned scroll listeners.
 */
export function cleanupAnimations(triggers: ScrollTrigger[]): void {
  try {
    triggers.forEach((trigger) => {
      if (trigger && typeof trigger.kill === 'function') {
        trigger.kill();
      }
    });
  } catch {
    // Cleanup failure is non-critical
  }
}
